export const userRoles = [
  "DESIGNER",
  "B2B_BUYER",
  "MANUFACTURER",
  "PRODUCTION_MANAGER",
  "ADMIN",
] as const;
export type UserRole = (typeof userRoles)[number];

export const orderStatuses = [
  "PENDING",
  "UNDER_REVIEW",
  "NEGOTIATING",
  "IN_PROGRESS",
  "APPROVED",
  "CANCELLED",
  "DECLINED",
] as const;
export type OrderStatus = (typeof orderStatuses)[number];

export const productionStatuses = [
  "CHECKOUT_PENDING",
  "PAYMENT_SECURED",
  "ROUTING",
  "MANUFACTURING",
  "QA_REVIEW",
  "SHIPPED",
  "CANCELLED",
  "PRODUCTION_REVIEW",
  "QUOTE_REVISION",
] as const;
export type ProductionStatus = (typeof productionStatuses)[number];

export const fulfillmentPhases = [
  "INITIATION_PROOFING",
  "PROTOTYPING_COMMITMENT",
  "ACTIVE_PRODUCTION",
  "QA_FULFILLMENT",
] as const;
export type FulfillmentPhase = (typeof fulfillmentPhases)[number];

export const productionNodeStatuses = ["PENDING", "IN_PROGRESS", "BLOCKED", "COMPLETED"] as const;
export type ProductionNodeStatus = (typeof productionNodeStatuses)[number];

export const productionExceptionTypes = ["INTERNAL_BLOCKER", "EXTERNAL_BLOCKER"] as const;
export type ProductionExceptionType = (typeof productionExceptionTypes)[number];

export const productionExceptionStatuses = ["OPEN", "RESOLVED", "DISMISSED"] as const;
export type ProductionExceptionStatus = (typeof productionExceptionStatuses)[number];

export const clientActionTypes = [
  "APPROVE_DIGITAL_PROOF",
  "APPROVE_PHYSICAL_SAMPLE",
  "EXTERNAL_BLOCKER_DECISION",
] as const;
export type ClientActionType = (typeof clientActionTypes)[number];

export const clientActionStatuses = ["PENDING", "COMPLETED", "DECLINED"] as const;
export type ClientActionStatus = (typeof clientActionStatuses)[number];

export const designPublicationStatuses = ["DRAFT", "IN_REVIEW", "PUBLISHED", "REJECTED", "BUYER_BRIEF", "BUYER_FINALIZED"] as const;
export type DesignPublicationStatus = (typeof designPublicationStatuses)[number];

export const ipTiers = ["STANDARD", "EXCLUSIVE", "VAULT"] as const;
export type IpTier = (typeof ipTiers)[number];

export type DesignParametricMetadata = {
  metalWeightGrams: number;
  stoneCount: number;
  complexityScore?: number;
  stones?: Array<{ type: string; estimatedCount: number }>;
  primaryMetalType?: string;
  bomSource?: "manual" | "cad_analysis";
};

export type ManufacturingAnalysis = {
  jewelryType: string;
  primaryMethod: string;
  settingTypes: string[];
  finishingTechniques: string[];
  metals: Array<{
    type: string;
    estimatedWeightGrams?: number;
  }>;
  stones: Array<{
    type: string;
    cut: string;
    estimatedCount: number;
    settingType: string;
  }>;
  findings: string[];
  complexityScore: number;
  dfmNotes: string[];
  confidence: "high" | "medium" | "low";
};

export type DesignStoryKeywordGroup = {
  group: string;
  keywords: string[];
};

export type DesignStoryAngle = {
  title: string;
  thesis: string;
  suggestedKeywords: string[];
};

export type DesignStorySection = {
  id: string;
  heading: string;
  body: string;
};

export type DesignStoryLayoutSection = {
  type: "hero" | "narrative" | "origin" | "process" | "details" | "resolution" | "gallery" | "making";
  headline: string;
  copy: string;
  imageId: string;
  imageRole: string;
  layoutHint: string;
};

export type DesignStoryLayout = {
  pageMood: string;
  sections: DesignStoryLayoutSection[];
  accessibilityNotes: {
    contrastGuidance: string;
    altTextStyle: string;
    readingFlow: string;
  };
};

export type DesignStoryImage = {
  imageId: string;
  role: string;
  imageUrl: string;
};

export type LookbookImage = {
  imageId: string;
  variationKey: string;
  prompt: string;
  imageKey: string;
  aspectRatio?: string;
  category?: "product" | "editorial";
  generatedAt: string;
};

export type CaraConversationEntry = {
  role: "user" | "assistant" | "tool";
  content: string;
  timestamp?: string;
};

export type DesignStoryDraft = {
  photoReferences: string[];
  inspirationNote?: string;
  keywordGroups: DesignStoryKeywordGroup[];
  storyAngles: DesignStoryAngle[];
  selectedKeywords: string[];
  storyTitle?: string;
  storyText?: string;
  storySections: DesignStorySection[];
  layout?: DesignStoryLayout;
  generatedImages: DesignStoryImage[];
  materials?: string;
  sketchImageKeys?: string[];
  caraSessionId?: string;
  caraConversation?: CaraConversationEntry[];
  updatedAt: Date;
};

// ── Jewelry Dimensions (Industry Standard) ──────────────────────────────────

export const jewelryDimensionUnits = ["mm", "cm", "in"] as const;
export type JewelryDimensionUnit = (typeof jewelryDimensionUnits)[number];

export const jewelryTypes = [
  "ring", "necklace", "bracelet", "bangle", "earring", "pendant",
  "brooch", "anklet", "cufflink", "tiara", "charm", "other",
] as const;
export type JewelryType = (typeof jewelryTypes)[number];

export const ringSizeStandards = ["US", "UK", "EU", "JP", "internal_diameter_mm"] as const;
export type RingSizeStandard = (typeof ringSizeStandards)[number];

export type RingSize = {
  standard: RingSizeStandard;
  value: string;
};

export type ImageCalibration = {
  imageKey: string;
  point1: { x: number; y: number };
  point2: { x: number; y: number };
  referenceDistanceMm: number;
  pixelsPerMm: number;
  calibratedBy: string;
  calibratedAt: string;
  referenceType?: "ruler" | "coin" | "caliper" | "custom";
  notes?: string;
};

export type JewelryDimensions = {
  unit: JewelryDimensionUnit;
  overallLengthMm?: number;
  overallWidthMm?: number;
  overallHeightMm?: number;
  depthMm?: number;

  innerDiameterMm?: number;
  innerCircumferenceMm?: number;
  bandWidthMm?: number;
  bandThicknessMm?: number;
  ringSize?: RingSize;

  chainLengthMm?: number;
  chainWidthMm?: number;
  claspLengthMm?: number;
  pendantDropMm?: number;

  postLengthMm?: number;
  backingDiameterMm?: number;

  settingDiameterMm?: number;
  settingDepthMm?: number;
  centerStoneSizeMm?: { length: number; width: number; depth?: number };

  totalWeightGrams?: number;
  metalWeightGrams?: number;
  stoneWeightCarats?: number;

  calibration?: ImageCalibration;
  measuredBy?: string;
  measuredAt?: string;
  verifiedBy?: string;
  verifiedAt?: string;
};

export type PieceFingerprint = {
  jewelryType: string;
  metalTone: string;
  primaryStoneType: string;
  stoneArrangement: string;
  silhouetteShape: string;
  settingStyle: string;
  symmetryPattern: string;
  distinctiveFeatures: string[];
};

export type Design = {
  id: string;
  designerId: string;
  originType?: "designer" | "buyer_cara" | "buyer_customization";
  originBuyerId?: string;
  title: string;
  description: string;
  renderUrl2d: string;
  catalogImageKey?: string;
  lookbookImages?: LookbookImage[];
  viewerAssetKey: string;
  sourceCadKey: string;
  basePriceUsd: number;
  ipTier: IpTier;
  publicationStatus: DesignPublicationStatus;
  isFeatured: boolean;
  storyDraft?: DesignStoryDraft;
  submittedForApprovalAt?: Date;
  approvedAt?: Date;
  rejectedAt?: Date;
  rejectionReason?: string;
  parametricMetadata: DesignParametricMetadata;
  manufacturingAnalysis?: ManufacturingAnalysis;
  imageHash?: string;
  perceptualHash?: string;
  pieceFingerprint?: PieceFingerprint;
  dimensions?: JewelryDimensions;
};

export type OrderEntity = {
  id: string;
  buyerId: string;
  designId: string;
  status: OrderStatus;
  proposedFeeUsd: number;
  customizationBrief: string;
  stripeHoldId?: string | null;
  latestRevisionAssetKey?: string | null;
  createdAt: Date;
  updatedAt: Date;
};

export type ProductionConfiguration = {
  metalType: "14K_GOLD" | "18K_GOLD" | "PLATINUM" | "STERLING_SILVER";
  stoneQuality: "STANDARD" | "PREMIUM" | "HEIRLOOM";
  ringSize: string;
};

export type ProductionEntity = {
  id: string;
  buyerId: string;
  designId: string;
  technicalDesignerId?: string | null;
  manufacturerId?: string | null;
  status: ProductionStatus;
  fulfillmentPhase: FulfillmentPhase;
  workflowTemplateId?: string | null;
  pipelinePaused: boolean;
  pipelinePausedReason?: string;
  workflowMilestones: {
    digitalProofUrl?: string;
    digitalProofSubmittedAt?: Date;
    digitalProofApprovedAt?: Date;
    physicalSampleUrl?: string;
    physicalSampleSubmittedAt?: Date;
    physicalSampleApprovedAt?: Date;
    nreInvoiceAmountUsd?: number;
    nreInvoiceIssuedAt?: Date;
    nreInvoicePaidAt?: Date;
    proformaInvoiceAmountUsd?: number;
    proformaInvoiceIssuedAt?: Date;
    depositPaidUsd?: number;
    depositPaidAt?: Date;
    finalInvoiceAmountUsd?: number;
    finalInvoiceIssuedAt?: Date;
    finalBalancePaidAt?: Date;
  };
  configuration: ProductionConfiguration;
  shippingAddress: {
    fullName: string;
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postalCode?: string;
    country: string;
    email: string;
  };
  totalPriceUsd: number;
  stripePaymentIntentId?: string | null;
  quantity?: number | null;
  holdPaymentIntentId?: string | null;
  quotedPriceUsd?: number | null;
  revisedPriceUsd?: number | null;
  revisionReason?: string | null;
  holdExpiresAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
};

export type WorkflowTemplate = {
  id: string;
  name: string;
  category: string;
  active: boolean;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
};

export type WorkflowTemplateNode = {
  id: string;
  templateId: string;
  orderIndex: number;
  name: string;
  description?: string;
  expectedDurationHours?: number;
  createdAt: Date;
  updatedAt: Date;
};

export type ProductionNode = {
  id: string;
  productionId: string;
  templateNodeId?: string | null;
  orderIndex: number;
  name: string;
  assignedVendorId?: string | null;
  quotedCostUsd?: number;
  actualCostUsd?: number;
  variancePercentage?: number;
  costVarianceReasonCode?: string;
  costVarianceReasonNote?: string;
  quoteDocumentKey?: string;
  poDocumentKey?: string;
  finalInvoiceKey?: string;
  shippingWaybillKey?: string;
  quoteDocumentUrl?: string;
  poDocumentUrl?: string;
  finalInvoiceUrl?: string;
  shippingWaybillUrl?: string;
  inventoryReceivedAt?: Date;
  inventoryReceivedBy?: string;
  status: ProductionNodeStatus;
  startedAt?: Date;
  completedAt?: Date;
  blockedReason?: string;
  createdAt: Date;
  updatedAt: Date;
};

export type ProductionException = {
  id: string;
  productionId: string;
  nodeId?: string | null;
  type: ProductionExceptionType;
  status: ProductionExceptionStatus;
  reason: string;
  etaImpactDays: number;
  requiresDesignerAction?: boolean;
  designerActionStatus?: "PENDING" | "COMPLETED";
  routedToRole?: "TECHNICAL_DESIGNER" | "FACTORY" | "PRODUCTION_MANAGER" | "CLIENT";
  createdBy: string;
  resolvedBy?: string;
  resolutionNote?: string;
  createdAt: Date;
  resolvedAt?: Date;
};

export type ProductionClientAction = {
  id: string;
  productionId: string;
  type: ClientActionType;
  status: ClientActionStatus;
  prompt: string;
  options?: string[];
  requiredBy?: Date;
  responseValue?: string;
  completedBy?: string;
  completedAt?: Date;
  createdAt: Date;
};

// ── Collection Types ────────────────────────────────────────────────────────

export type CollectionStoryKeywordGroup = {
  group: string;
  keywords: string[];
};

export type CollectionStoryAngle = {
  title: string;
  thesis: string;
  suggestedKeywords: string[];
};

export type CollectionStorySection = {
  id: string;
  heading: string;
  body: string;
};

export type CollectionStoryLayoutSection = {
  type: "hero" | "overview" | "piece-spotlight" | "connection" | "details" | "resolution" | "gallery";
  headline: string;
  copy: string;
  imageId: string;
  imageRole: string;
  layoutHint: string;
  spotlightDesignId?: string;
};

export type CollectionStoryLayout = {
  pageMood: string;
  sections: CollectionStoryLayoutSection[];
  accessibilityNotes: {
    contrastGuidance: string;
    altTextStyle: string;
    readingFlow: string;
  };
};

export type CollectionStoryImage = {
  imageId: string;
  role: string;
  imageUrl: string;
};

export type CollectionStoryDraft = {
  photoReferences: string[];
  inspirationNote?: string;
  keywordGroups: CollectionStoryKeywordGroup[];
  storyAngles: CollectionStoryAngle[];
  selectedKeywords: string[];
  storyTitle?: string;
  storyText?: string;
  storySections: CollectionStorySection[];
  layout?: CollectionStoryLayout;
  generatedImages: CollectionStoryImage[];
  updatedAt: Date;
};

export type CollectionEntity = {
  id: string;
  designerId: string;
  title: string;
  description: string;
  coverImageUrl: string;
  publicationStatus: DesignPublicationStatus;
  isFeatured: boolean;
  storyDraft?: CollectionStoryDraft;
  designIds: string[];
  submittedForApprovalAt?: Date;
  approvedAt?: Date;
  rejectedAt?: Date;
  rejectionReason?: string;
  createdAt: Date;
  updatedAt: Date;
};

export type ManufacturerProfile = {
  userId: string;
  displayName: string;
  region: string;
  specialties: Array<"RING" | "NECKLACE" | "BRACELET" | "EARRING">;
  activeCapacity: number;
  maxCapacity: number;
  qualityScore: number;
};

// ── Photo Studio SaaS Types ─────────────────────────────────────────────────

export const photoStudioPlans = ["free", "starter", "pro", "business"] as const;
export type PhotoStudioPlan = (typeof photoStudioPlans)[number];

export const subscriptionStatuses = ["active", "canceled", "past_due", "trialing"] as const;
export type SubscriptionStatus = (typeof subscriptionStatuses)[number];

export const photoStudioJobStatuses = ["pending", "processing", "completed", "failed"] as const;
export type PhotoStudioJobStatus = (typeof photoStudioJobStatuses)[number];

export type Subscription = {
  id: string;
  userId: string;
  stripeCustomerId: string;
  stripeSubscriptionId?: string;
  plan: PhotoStudioPlan;
  status: SubscriptionStatus;
  currentPeriodEnd?: Date;
  designsUsedThisPeriod: number;
  createdAt: Date;
  updatedAt: Date;
};

export type ApiKey = {
  id: string;
  userId: string;
  keyPrefix: string;
  label: string;
  lastUsedAt?: Date;
  revokedAt?: Date;
  createdAt: Date;
};

export type PhotoStudioJob = {
  id: string;
  userId: string;
  apiKeyId: string;
  sourceImageKey: string;
  status: PhotoStudioJobStatus;
  catalogImageKey?: string;
  lookbookImages: LookbookImage[];
  pieceTitle: string;
  pieceDescription: string;
  createdAt: Date;
  completedAt?: Date;
  publishedDesignId?: string;
};

export const PLAN_LIMITS: Record<PhotoStudioPlan, number> = {
  free: 3,
  starter: 15,
  pro: 60,
  business: Infinity,
};

// ── Chat Types ──────────────────────────────────────────────────────────────

export const conversationContextTypes = ["ORDER", "PRODUCTION", "GENERAL"] as const;
export type ConversationContextType = (typeof conversationContextTypes)[number];

export const messageTypes = ["TEXT", "IMAGE", "FILE", "SYSTEM", "QUOTE_PROPOSAL", "STATUS_UPDATE"] as const;
export type MessageType = (typeof messageTypes)[number];

export type Conversation = {
  id: string;
  title?: string;
  contextType: ConversationContextType;
  contextId?: string;
  createdAt: Date;
  updatedAt: Date;
};

export type ConversationParticipant = {
  id: string;
  conversationId: string;
  userId: string;
  joinedAt: Date;
  lastReadAt: Date;
  locale: string;
};

export type ChatMessage = {
  id: string;
  conversationId: string;
  authorId: string;
  type: MessageType;
  body: string;
  metadata: Record<string, unknown>;
  createdAt: Date;
};

export type MessageTranslation = {
  id: string;
  messageId: string;
  locale: string;
  translatedBody: string;
  createdAt: Date;
};

// ── Buyer Catalog Subscription Types ────────────────────────────────────────

export const buyerPlanTiers = ["explorer", "professional", "atelier", "enterprise"] as const;
export type BuyerPlanTier = (typeof buyerPlanTiers)[number];

export const billingIntervals = ["monthly", "annual"] as const;
export type BillingInterval = (typeof billingIntervals)[number];

export type BuyerSubscription = {
  id: string;
  userId: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  plan: BuyerPlanTier;
  billingInterval: BillingInterval;
  status: SubscriptionStatus;
  currentPeriodEnd?: Date;
  createdAt: Date;
  updatedAt: Date;
};

// ── Studio Credits ───────────────────────────────────────────────────────────

export const STUDIO_CREDIT_COSTS = {
  essentialsKit: 10,
  caratzonKit: 100,
  singlePhotoRegen: 20,
  lookbookPdf: 50,
  caraMessage: 5,
  caraSketch: 5,
  caraImage: 15,
  caraLookbook: 15,
  caraVideo: 25,
} as const;

export type StudioCreditAction = keyof typeof STUDIO_CREDIT_COSTS;

export const topUpPackIds = ["starter", "pro", "studio"] as const;
export type TopUpPackId = (typeof topUpPackIds)[number];

export type TopUpPack = {
  id: TopUpPackId;
  credits: number;
  priceUsd: number;
};

export const TOP_UP_PACKS: Record<TopUpPackId, TopUpPack> = {
  starter: { id: "starter", credits: 500, priceUsd: 59 },
  pro: { id: "pro", credits: 1_000, priceUsd: 99 },
  studio: { id: "studio", credits: 2_500, priceUsd: 199 },
};

// ── Buyer Access ─────────────────────────────────────────────────────────────

export type BuyerAccess = {
  plan: BuyerPlanTier;
  catalogAccess: "featured_only" | "full";
  searchEnabled: boolean;
  aiSearchEnabled: boolean;
  viewerEnabled: boolean;
  sourcingEnabled: boolean;
  messagingEnabled: boolean;
  allowedIpTiers: IpTier[];
  priceMonthly: number;
  priceAnnual: number;
  /** Monthly Studio Credit allowance (resets each billing cycle, unused credits expire) */
  studioCreditsMonthly: number;
  /** One-time welcome credits for new accounts (Explorer only) */
  welcomeCredits: number;
  lookbookPdfEnabled: boolean;
  inspirePublishEnabled: boolean;
  inspirePublishLimit: number;
};

export const BUYER_PLAN_FEATURES: Record<BuyerPlanTier, BuyerAccess> = {
  explorer: {
    plan: "explorer",
    catalogAccess: "featured_only",
    searchEnabled: false,
    aiSearchEnabled: false,
    viewerEnabled: false,
    sourcingEnabled: false,
    messagingEnabled: false,
    allowedIpTiers: [],
    priceMonthly: 0,
    priceAnnual: 0,
    studioCreditsMonthly: 0,
    welcomeCredits: 200,
    lookbookPdfEnabled: false,
    inspirePublishEnabled: false,
    inspirePublishLimit: 0,
  },
  professional: {
    plan: "professional",
    catalogAccess: "full",
    searchEnabled: true,
    aiSearchEnabled: false,
    viewerEnabled: true,
    sourcingEnabled: true,
    messagingEnabled: true,
    allowedIpTiers: ["STANDARD", "EXCLUSIVE"],
    priceMonthly: 149,
    priceAnnual: 1490,
    studioCreditsMonthly: 1_500,
    welcomeCredits: 0,
    lookbookPdfEnabled: false,
    inspirePublishEnabled: true,
    inspirePublishLimit: 3,
  },
  atelier: {
    plan: "atelier",
    catalogAccess: "full",
    searchEnabled: true,
    aiSearchEnabled: true,
    viewerEnabled: true,
    sourcingEnabled: true,
    messagingEnabled: true,
    allowedIpTiers: ["STANDARD", "EXCLUSIVE", "VAULT"],
    priceMonthly: 399,
    priceAnnual: 3990,
    studioCreditsMonthly: 5_000,
    welcomeCredits: 0,
    lookbookPdfEnabled: true,
    inspirePublishEnabled: true,
    inspirePublishLimit: Infinity,
  },
  enterprise: {
    plan: "enterprise",
    catalogAccess: "full",
    searchEnabled: true,
    aiSearchEnabled: true,
    viewerEnabled: true,
    sourcingEnabled: true,
    messagingEnabled: true,
    allowedIpTiers: ["STANDARD", "EXCLUSIVE", "VAULT"],
    priceMonthly: 0,
    priceAnnual: 0,
    studioCreditsMonthly: 0,
    welcomeCredits: 0,
    lookbookPdfEnabled: true,
    inspirePublishEnabled: true,
    inspirePublishLimit: Infinity,
  },
};

// ── Academy ──────────────────────────────────────────────────────────────────

export type ArticleCategory =
  | "manufacturing"
  | "business"
  | "trends"
  | "wholesale"
  | "brand"
  | "tech"
  | "operations";

export type ArticleImage = {
  id: string;
  key: string;
  alt: string;
  placement: string;
};

export type AcademyArticle = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  category: ArticleCategory;
  tags: string[];
  ogImage: string | null;
  images: ArticleImage[];
  authorName: string;
  readingTimeMinutes: number;
  isFeatured: boolean;
  publishedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
};

// ── Newsroom ─────────────────────────────────────────────────────────────────

export type NewsroomCategory =
  | "company"
  | "product"
  | "partnership"
  | "industry"
  | "press_release";

export type NewsroomArticle = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  category: NewsroomCategory;
  tags: string[];
  ogImage: string | null;
  images: ArticleImage[];
  authorName: string;
  readingTimeMinutes: number;
  isFeatured: boolean;
  publishedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
};

// ── Inspire Listings ─────────────────────────────────────────────────────────

export const inspireCategories = [
  "rings", "necklaces", "earrings", "bracelets",
  "brooches", "watches", "sets", "other",
] as const;
export type InspireCategory = (typeof inspireCategories)[number];

export type InspireSave = {
  id: string;
  userId: string;
  listingId: string;
  createdAt: Date;
};

export type InspireListingSource = "design" | "demo";

export const inspireApprovalStatuses = ["approved", "pending_review", "rejected"] as const;
export type InspireApprovalStatus = (typeof inspireApprovalStatuses)[number];

export type InspireListing = {
  id: string;
  designId: string | null;

  source: InspireListingSource;
  demoSessionId: string | null;
  demoStoryId: string | null;
  creatorName: string | null;

  slug: string | null;
  isPublished: boolean;
  approvalStatus: InspireApprovalStatus;
  publishedAt: Date | null;

  heroImageKey: string | null;
  category: InspireCategory | null;
  materials: string[];
  styleKeywords: string[];
  viewCount: number;

  productDescription: string | null;
  brandStory: string | null;
  socialCaptions: { platform: string; text: string }[];

  createdAt: Date;
  updatedAt: Date;
};

/** Inspire listing joined with its design's visual/metadata fields for display */
export type InspireListingWithDesign = InspireListing & {
  designTitle: string;
  designDescription: string;
  designerName: string;
  designerId: string;
  catalogImageKey: string | null;
  lookbookImages: LookbookImage[];
  renderUrl2d: string;
  storyDraft: DesignStoryDraft | null;
  basePriceUsd: number;
  ipTier: string;
  isFeatured: boolean;
};

// ── Catalog Listings (trade channel) ────────────────────────────────────────

export type LicensePricing = {
  standardFeeUsd: number | null;
  exclusiveFeeUsd: number | null;
  vaultFeeUsd: number | null;
};

export const CATALOG_TERMS_VERSION = "2026-03-04-v1";
export const CATALOG_PLATFORM_FEE_PERCENT = 20;

export type CatalogSubmissionStatus = "DRAFT" | "PENDING_REVIEW" | "APPROVED" | "REJECTED";

export type CatalogListing = {
  id: string;
  designId: string;

  isPublished: boolean;
  publishedAt: Date | null;

  tradePriceUsd: number | null;
  licensePricing: LicensePricing | null;
  embargoUntil: Date | null;
  availableFrom: Date | null;
  availableUntil: Date | null;
  minimumOrderQuantity: number;
  leadTimeDays: number | null;
  tradeNotes: string | null;

  submittedByDesignerId: string | null;
  catalogTermsAcceptedAt: Date | null;
  catalogTermsVersion: string | null;
  submissionStatus: CatalogSubmissionStatus;

  createdAt: Date;
  updatedAt: Date;
};

/** Catalog listing joined with design fields for admin/trade display */
export type CatalogListingWithDesign = CatalogListing & {
  designTitle: string;
  designDescription: string;
  designerName: string;
  designerId: string;
  basePriceUsd: number;
  ipTier: string;
  isFeatured: boolean;
  catalogImageKey: string | null;
  renderUrl2d: string;
};

// ── Production Planning (AI PM Agent) ────────────────────────────────────────

export const productionPlanStatuses = [
  "DRAFT", "PENDING_REVIEW", "APPROVED", "EXECUTING", "COMPLETED", "CANCELLED",
] as const;
export type ProductionPlanStatus = (typeof productionPlanStatuses)[number];

export const subOrderStatuses = [
  "PENDING", "QUOTED", "ASSIGNED", "IN_PROGRESS", "QA", "COMPLETED", "BLOCKED", "CANCELLED",
] as const;
export type SubOrderStatus = (typeof subOrderStatuses)[number];

export const subOrderCategories = [
  "CAD_MODELING", "CAD_REVISION", "MASTER_MODEL", "MOLD_MAKING", "CASTING",
  "STONE_SOURCING", "STONE_CUTTING", "SETTING", "ASSEMBLY", "FINISHING",
  "PLATING", "ENGRAVING", "ENAMELING", "QA_INSPECTION", "PHOTOGRAPHY",
  "PACKAGING", "SHIPPING",
] as const;
export type SubOrderCategory = (typeof subOrderCategories)[number];

export type ProductionPlanRisk = {
  risk: string;
  severity: "low" | "medium" | "high";
  mitigation: string;
};

export type MaterialRequirement = {
  material: string;
  specification: string;
  quantity: string;
  estimatedCostUsd: number;
};

export type QualityCheckpoint = {
  afterStep: string;
  checks: string[];
};

export type ProductionPlanAnalysis = {
  jewelryType: string;
  complexity: "low" | "medium" | "high" | "very_high";
  estimatedTotalDays: number;
  estimatedTotalCostUsd: number;
  criticalPath: string[];
  risks: ProductionPlanRisk[];
  materialRequirements: MaterialRequirement[];
  specialEquipment: string[];
  qualityCheckpoints: QualityCheckpoint[];
};

export type SubOrderDeliverable = {
  type: string;
  key?: string;
  url?: string;
  description: string;
};

export type ProductionPlanSubOrder = {
  id: string;
  planId: string;
  orderIndex: number;
  category: SubOrderCategory;
  title: string;
  description?: string;
  specifications?: Record<string, unknown>;
  dependsOn: string[];
  assignedManufacturerId?: string;
  assignedManufacturerName?: string;
  conversationId?: string;
  status: SubOrderStatus;
  estimatedDurationDays?: number;
  estimatedCostUsd?: number;
  actualCostUsd?: number;
  quotedCostUsd?: number;
  startedAt?: Date;
  completedAt?: Date;
  blockedReason?: string;
  notes?: string;
  deliverables?: SubOrderDeliverable[];
  createdAt: Date;
  updatedAt: Date;
};

export type ProductionPlanEntity = {
  id: string;
  productionId?: string;
  designId: string;
  orderId?: string;
  createdBy: string;
  status: ProductionPlanStatus;
  title: string;
  summary?: string;
  aiAnalysis?: ProductionPlanAnalysis;
  conversationId?: string;
  totalEstimatedCostUsd?: number;
  totalEstimatedDays?: number;
  approvedAt?: Date;
  approvedBy?: string;
  subOrders: ProductionPlanSubOrder[];
  createdAt: Date;
  updatedAt: Date;
};

export const SUB_ORDER_CATEGORY_LABELS: Record<SubOrderCategory, string> = {
  CAD_MODELING: "CAD Modeling",
  CAD_REVISION: "CAD Revision",
  MASTER_MODEL: "Master Model / Prototype",
  MOLD_MAKING: "Rubber Mold Making",
  CASTING: "Lost-Wax Casting",
  STONE_SOURCING: "Stone Sourcing",
  STONE_CUTTING: "Stone Cutting & Calibration",
  SETTING: "Stone Setting",
  ASSEMBLY: "Assembly & Soldering",
  FINISHING: "Surface Finishing & Polishing",
  PLATING: "Plating / Rhodium / PVD",
  ENGRAVING: "Engraving / Laser Marking",
  ENAMELING: "Enameling",
  QA_INSPECTION: "QA Inspection",
  PHOTOGRAPHY: "Product Photography",
  PACKAGING: "Packaging & Labeling",
  SHIPPING: "Shipping & Logistics",
};

// ── Realtime Events ─────────────────────────────────────────────────────────

export const REALTIME_CHANNEL = "caratzon:events";

export type RealtimeEvent = {
  type: string;
  designId?: string;
  designerId?: string;
  timestamp: string;
  payload?: Record<string, unknown>;
};

export const RealtimeEventTypes = {
  DESIGN_INTAKE_COMPLETE: "design.intake.complete",
  DESIGN_BACKFILL_COMPLETE: "design.backfill.complete",
  CATALOG_PHOTO_COMPLETE: "design.catalog-photo.complete",
  LOOKBOOK_IMAGE_COMPLETE: "design.lookbook-image.complete",
  LOOKBOOK_COMPLETE: "design.lookbook.complete",
  CAD_ANALYSIS_COMPLETE: "design.cad-analysis.complete",
  STORY_COMPLETE: "design.story.complete",
  DESIGN_PUBLISHED: "design.published",
  DESIGN_GENERATION_COMPLETE: "design.generation.complete",
  // CAD Pipeline
  CAD_DECOMPOSITION_COMPLETE: "design.cad-decomposition.complete",
  CAD_COMPONENT_COMPLETE: "design.cad-component.complete",
  CAD_ASSEMBLY_COMPLETE: "design.cad-assembly.complete",
  CAD_MODELING_COMPLETE: "design.cad-modeling.complete",
  // Production Dashboard
  SUB_ORDER_UPDATED: "production.sub-order.updated",
} as const;

export type CadComponentConfidence = "high" | "medium" | "low";
export type CadOverallComplexity = "simple" | "moderate" | "complex";

export interface CadComponentSpec {
  id: string;
  type: string;
  subtype: string;
  confidence: CadComponentConfidence;
  dimensions: Record<string, number>;
  position: { x: number; y: number; z: number };
  orientation: { axis: string; angle: number };
  connectsTo: string[];
  notes: string;
}

export interface CadUnknownFeature {
  description: string;
  confidence: "medium" | "low";
  boundingBox: { width: number; height: number; depth: number };
  position: { x: number; y: number; z: number };
  referencePhotoIndex: number;
  suggestedApproach: string;
}

export interface CadDecompositionManifest {
  components: CadComponentSpec[];
  unknownFeatures: CadUnknownFeature[];
  assemblyNotes: string;
  overallComplexity: CadOverallComplexity;
}

export interface CadComponentResult {
  componentId: string;
  status: "success" | "partial" | "failed";
  stepFileKey?: string;
  confidence: CadComponentConfidence;
  attempts: number;
  errors: string[];
}

export interface CadPackageDeliverables {
  stepFileKey: string;
  specSheetKey: string;
  componentMapKey: string;
  buildLogKey: string;
  partialResult: boolean;
  failedComponents: string[];
  flaggedComponents: string[];
}
